import { defaultError } from '@qpr/client-library';
import api, { ApiRequest, ApiResult } from '../../../../api/api-caller';
import initialState, { PropertiesState } from '../properties-state';

export interface Property {
  attributeName: string;
  viewName?: string;
}

const fetchDiagramUrl = async (item: PropertiesViewModelProps): Promise<ApiResult> => {
  const query: ApiRequest = {
    body: {
      query: `[${item.id}]`,
      attributes: 'flowchartUrl'
    },
    method: 'POST'
  };
  const diagramResult: ApiResult = await api.asyncCallApi('meaquery', query);

  return diagramResult;
};

const getDiagramUrl = (result: ApiResult) => {
  const { data } = result;
  const { flowchartUrl } = data[0];

  return flowchartUrl;
};

const fetchDiagramImage = async (item: PropertiesViewModelProps): Promise<ApiResult> => {
  const graphDataQuery: ApiRequest = {
    body: {
      elementid: item.id
    },
    method: 'POST'
  };
  const result: ApiResult = await api.asyncCallApi('graph', graphDataQuery);
  return result;
};

// eslint-disable-next-line consistent-return
const getDiagramImage = (diagramResult: ApiResult) => {
  if (diagramResult.data) {
    const { buffer } = new Uint8Array(diagramResult.data.content);
    const blob = new Blob([buffer]);
    return URL.createObjectURL(blob);
  }
};

const fetchProperties = async (item: PropertiesViewModelProps): Promise<ApiResult> => {
  const attributeList = item.propertyList.map((singleProperty: Property) => {
    return singleProperty.attributeName;
  });

  const uniqueList = attributeList
    .filter((attribute: string, index: number) => attributeList.indexOf(attribute) === index)
    .join(',');

  const query: ApiRequest = {
    body: {
      query: `[${item.id}]`,
      attributes: uniqueList
    },
    method: 'POST'
  };
  const propertiesResult: ApiResult = await api.asyncCallApi('meaquery', query);

  return propertiesResult;
};

const fetchDocuments = async (item: PropertiesViewModelProps): Promise<ApiResult> => {
  const query: ApiRequest = {
    body: {
      query: `[${item.id}].informationitems`,
      attributes: 'name,infoitem.linklocation(as=link)',
      criteria: 'infoitem.isembedded=0  AND infoitem.linklocation <> undefined'
    },
    method: 'POST'
  };
  const documentsResult: ApiResult = await api.asyncCallApi('meaquery', query);

  return documentsResult;
};

const getDocuments = (documentsResult: ApiResult) => {
  const { data } = documentsResult;

  return data;
};

const getProperties = (propertiesResult: ApiResult, item: PropertiesViewModelProps) => {
  const data = propertiesResult.data[0];

  const properties: Record<string, string> = {};

  item.propertyList.forEach((element: Property) => {
    if (data[element.attributeName])
      properties[element.viewName || element.attributeName] = data[element.attributeName];
    else if (element.viewName || element.attributeName !== 'Description') {
      properties[element.viewName || element.attributeName] = 'No value';
    } else {
      properties[element.attributeName] = '';
    }
  });

  return properties;
};

const fetchMetrics = async (item: PropertiesViewModelProps): Promise<ApiResult> => {
  let query = `@links = [${item.id}].scorecardlinks;`;
  query += '@children = @links.childobjects;';
  query += '@links += @children;';
  query += '@links;';

  const meaQuery: ApiRequest = {
    body: {
      query,
      sortby: 'level,name',
      attributes:
        'name, prettyvalue(as="value"), prettyvalue(as="targetvalue",series="TAR"), rangecolor, periodname'
    },
    method: 'POST'
  };
  const metricsResult: ApiResult = await api.asyncCallApi('meaquery', meaQuery);
  return metricsResult;
};

const getMeasures = (metricsResult: ApiResult) => {
  const { data } = metricsResult;

  const formatResult = () => {
    const parentMeasure = data[0];
    parentMeasure.children = [];
    parentMeasure.mainmeasure = true;
    parentMeasure.rangecolor = parentMeasure.rangecolor ? `#${parentMeasure.rangecolor}` : '';
    for (let i = 1; i < data.length; i += 1) {
      const child = data[i];
      child.children = [];
      child.rangecolor = child.rangecolor ? `#${child.rangecolor}` : '';
      parentMeasure.children.push(child);
    }
    return parentMeasure;
  };

  return data.length > 0 ? formatResult() : null;
};

const errorHandler = (apiResult: ApiResult): PropertiesState => {
  return {
    ...initialState,
    error: apiResult.error || defaultError
  };
};

const doGetData = async (item: PropertiesViewModelProps): Promise<PropertiesState> => {
  try {
    const [propertiesResult, documentsResult, metricsResult, diagramImageResult, diagramUrlResult] =
      await Promise.all([
        fetchProperties(item),
        fetchDocuments(item),
        fetchMetrics(item),
        fetchDiagramImage(item),
        fetchDiagramUrl(item)
      ]);

    if (!propertiesResult.ok) {
      return errorHandler(propertiesResult);
    }

    if (!documentsResult.ok) {
      return errorHandler(documentsResult);
    }

    if (!metricsResult.ok) {
      return errorHandler(metricsResult);
    }

    if (!diagramImageResult.ok) {
      return errorHandler(diagramImageResult);
    }

    if (!diagramUrlResult.ok) {
      return errorHandler(diagramUrlResult);
    }

    const properties: Record<string, string> = getProperties(propertiesResult, item);

    const documents: any = getDocuments(documentsResult);

    const measures: any = getMeasures(metricsResult);

    const flowchartImageUrl: any = getDiagramImage(diagramImageResult);

    const flowchartUrl: any = getDiagramUrl(diagramUrlResult);

    if (!item.propertyList.some((e: Property) => e.attributeName === 'id')) {
      delete properties.id;
    }

    return {
      ...initialState,
      data: { properties, documents, measures, diagram: { flowchartImageUrl, flowchartUrl } }
    };
  } catch (error) {
    return {
      ...initialState,
      error: { message: 'Server unreachable', details: JSON.stringify(error) }
    };
  }
};

// allow use string to access element
interface IData {
  [key: string]: any;
}
const viewModelCache: IData = {};

export interface PropertiesViewModelType {
  fetchData(): Promise<PropertiesState>;
  getData(): Promise<PropertiesState>;
  getId(): string;
}

interface PropertiesViewModelProps {
  propertyList: Property[];
  id: string;
}

const createPropertiesViewModel = (item: PropertiesViewModelProps): PropertiesViewModelType => {
  let data: PropertiesState;
  const cacheKey: string = JSON.stringify(item);

  const getId = (): string => item.id;
  const fetchData = async (): Promise<PropertiesState> => {
    if (viewModelCache[cacheKey]) {
      data = viewModelCache[cacheKey];
    } else {
      data = await doGetData(item);
      viewModelCache[cacheKey] = data;
    }
    return data;
  };

  const getData = async (): Promise<PropertiesState> => {
    if (data) {
      return data;
    }
    await fetchData();
    return data;
  };

  return {
    getId,
    fetchData,
    getData
  };
};

export default createPropertiesViewModel;
